import { Link } from '@tanstack/react-router'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { Archive, ArrowLeft, Linkedin, Loader2, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import {
  useOutreachArchiveQuery,
  usePortfolioArchiveQuery,
  useRestoreArchivedMutation,
  useRestorePortfolioArchivedMutation,
} from '../hooks/use-outreach-archive'

/**
 * One card shape for both campaigns. The two tracks store completely different
 * rows — investor leads vs portfolio messages — but a founder reading the
 * archive only wants "who, from where, and can I get them back".
 */
type ArchiveBucket = 'engaged' | 'skipped' | 'declined' | 'finished'

interface ArchiveEntry {
  key: string
  track: string
  name: string
  subtitle: string
  linkedinUrl: string | null
  status: ArchiveBucket
  onRestore: () => void
}

interface Props {
  startupId: string
}

export function OutreachArchiveView({ startupId }: Props) {
  const archiveQuery = useOutreachArchiveQuery(startupId)
  const portfolioArchiveQuery = usePortfolioArchiveQuery(startupId)
  const restore = useRestoreArchivedMutation(startupId)
  const restorePortfolio = useRestorePortfolioArchivedMutation(startupId)
  const [restoring, setRestoring] = useState<Record<string, boolean>>({})

  // Both tracks restore through different endpoints, so each entry carries its
  // own call; the spinner is keyed by entry so one restore never spins another.
  const markRestoring = (key: string, run: (onSettled: () => void) => void) => {
    setRestoring((prev) => ({ ...prev, [key]: true }))
    run(() =>
      setRestoring((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    )
  }

  const investorEntries: ArchiveEntry[] = (archiveQuery.data ?? [])
    .filter((item) => item.status !== 'approved' && item.status !== 'awaiting')
    .map((item) => {
      const key = `investor:${item.leadId}`
      return {
        key,
        track: 'Investor campaign',
        name: item.contact.name,
        subtitle: [item.contact.title, item.investor.name].filter(Boolean).join(' \u00b7 '),
        linkedinUrl: item.contact.linkedinUrl,
        status: item.status as ArchiveBucket,
        onRestore: () =>
          markRestoring(key, (onSettled) => restore.mutate(item.leadId, { onSettled })),
      }
    })

  const portfolioItems =
    portfolioArchiveQuery.data?.status === 'ok' ? portfolioArchiveQuery.data.data.items : []
  const portfolioEntries: ArchiveEntry[] = portfolioItems.map((item) => {
    const key = `warm:${item.id}`
    return {
      key,
      track: 'Warm intro campaign',
      name: item.name ?? 'Unknown',
      subtitle: [item.title, item.companyName, item.fundName].filter(Boolean).join(' \u00b7 '),
      linkedinUrl: item.linkedinUrl,
      status: item.archiveStatus,
      onRestore: () =>
        markRestoring(key, (onSettled) =>
          restorePortfolio.mutate({ path: { id: startupId, messageId: item.id } }, { onSettled })
        ),
    }
  })

  const entries = [...investorEntries, ...portfolioEntries]
  const isLoading = archiveQuery.isLoading || portfolioArchiveQuery.isLoading
  const inBucket = (status: ArchiveBucket) => entries.filter((e) => e.status === status)
  const handled = inBucket('engaged')
  const declined = inBucket('declined')
  const skipped = inBucket('skipped')
  const finished = inBucket('finished')

  return (
    <div className="mx-auto max-w-4xl py-6">
      <Link
        to="/app/startup/$id/outreach"
        params={{ id: startupId }}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to outreach
      </Link>

      <div className="mt-3 flex items-center gap-2.5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-card text-muted-foreground">
          <Archive className="size-4.5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Archive</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Handled, skipped, declined and no-response contacts. Restore one to put it back on the
            board.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      ) : entries.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border/50 px-6 py-12 text-center text-[13px] text-muted-foreground">
          Nothing archived yet. Contacts you skip or decline, and no-response contacts you archive,
          land here.
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          <ArchiveSection
            title="Handled"
            subtitle="You took these conversations over and cleared them. Restoring returns them to the Engaged list."
            entries={handled}
            restoring={restoring}
          />
          <ArchiveSection
            title="Skipped"
            subtitle="You passed on these for now. Skipping is not rejecting, so they stay here for you to bring back whenever you want."
            entries={skipped}
            restoring={restoring}
          />
          <ArchiveSection
            title="Declined"
            subtitle="You declined these. Restoring sends them back to your approval queue."
            entries={declined}
            restoring={restoring}
          />
          <ArchiveSection
            title="No response"
            subtitle="Full sequence ran with no reply. Restoring returns them to the Finished column."
            entries={finished}
            restoring={restoring}
          />
        </div>
      )}
    </div>
  )
}

function ArchiveSection({
  title,
  subtitle,
  entries,
  restoring,
}: {
  title: string
  subtitle: string
  entries: ArchiveEntry[]
  restoring: Record<string, boolean>
}) {
  if (entries.length === 0) return null
  return (
    <section>
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-border/60 bg-card px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            {title} · {entries.length}
          </span>
        </div>
        <p className="mt-1.5 text-[12px] text-muted-foreground">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {entries.map((entry) => (
          <ArchiveCard key={entry.key} entry={entry} restoring={restoring[entry.key] ?? false} />
        ))}
      </div>
    </section>
  )
}

function ArchiveCard({ entry, restoring }: { entry: ArchiveEntry; restoring: boolean }) {
  const initials = makeInitials(entry.name)
  return (
    <article className="flex items-start gap-2.5 rounded-lg border border-border/40 bg-background/60 px-3 py-2.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background/80 text-[10.5px] font-bold text-foreground/80">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold text-foreground">{entry.name}</span>
          <span className="shrink-0 rounded-full border border-border/50 px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide text-muted-foreground">
            {entry.track}
          </span>
        </div>
        <div className="truncate text-[11.5px] text-muted-foreground">{entry.subtitle}</div>
        {entry.linkedinUrl && (
          <a
            href={entry.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80"
          >
            <Linkedin className="size-3" />
            LinkedIn
          </a>
        )}
      </div>
      <button
        type="button"
        disabled={restoring}
        onClick={entry.onRestore}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-[11px] font-medium text-foreground/85 transition-colors hover:bg-card/70 disabled:opacity-50"
      >
        {restoring ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <RotateCcw className="size-3.5" />
        )}
        Restore
      </button>
    </article>
  )
}

function makeInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
}
